import React, { useState } from 'react';
import { FaShieldAlt, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const PrivacyPolicySection: React.FC = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setShowScrollIndicator(target.scrollTop > 20);
  };

  const sections = [
    {
      number: 1,
      icon: FaShieldAlt,
      title: 'Overview',
      content: `collectedAF LLC built the collectedAF app as a Free app. This SERVICE is provided by collectedAF LLC at no cost and is intended for use as is. This page is used to inform visitors regarding our policies with the collection, use, and disclosure of Personal Information if anyone decided to use our Service. If you choose to use our Service, then you agree to the collection and use of information in relation to this policy. The Personal Information that we collect is used for providing and improving the Service. We will not use or share your information with anyone except as described in this Privacy Policy.`
    },
    {
      number: 2,
      icon: FaShieldAlt,
      title: 'Data We Collect',
      content: `We collect the following types of information when you use our Service:

• Account information: full name, email address, phone number, age, and gender, collected during registration to create and manage your account.
• Profile and user-generated content: profile photos, images, and other content you upload to personalize your experience and interact with other users.
• Location data: precise and approximate location, including background location, used to power proximity-based features such as displaying nearby users, updating collections based on location, and enabling location-based interactions. With your consent, your approximate location may be shared with other users within the app.
• Device and log information: IP address, device name, operating system version, app configuration, and usage timestamps, collected automatically to diagnose errors, improve performance, and maintain the Service.
• SMS and verification data: phone number used for two-factor authentication (2FA) and identity verification via one-time SMS passcodes.

We do not collect health or medical data. The app does use third-party services that may collect information used to identify you.

You can enable or disable access to location data at any time through your device settings. collectedAF may collect location data while the app is in the background to enable continuous proximity-based features, even when the app is closed or not in use.`
    },
    {
      number: 3,
      icon: FaShieldAlt,
      title: 'How We Use Your Data',
      content: `We use the information we collect for the following purposes:

• To create, authenticate, and manage your account
• To provide, operate, and improve the features of our Service
• To enable location-based features and social interactions between users
• To send one-time passcodes for secure login via SMS
• To diagnose technical issues, monitor performance, and analyze usage patterns
• To enforce our terms of service and protect user safety
• To comply with legal obligations

We do not sell your personal information to third parties. We may share data with third-party service providers only as necessary to operate and improve our Service, and such providers are contractually obligated to protect your data and use it solely for the purposes we specify.`
    },
    {
      number: 4,
      icon: FaShieldAlt,
      title: 'Data Retention',
      content: `We retain your personal information, including account data, profile information, and location data, for as long as your account is active or as needed to provide you with our Service. If you choose to delete your account, we will delete or anonymize your personal data within 30 days, except where we are required to retain certain information to comply with legal obligations, resolve disputes, or enforce our agreements.

Log data and analytics information may be retained for up to 12 months for the purpose of improving our Service and diagnosing technical issues, after which it is automatically deleted or anonymized.`
    },
    {
      number: 5,
      icon: FaShieldAlt,
      title: 'Data Deletion',
      content: `You have the right to request deletion of your personal data at any time. You can delete your account and all associated data directly through the app's account settings. Alternatively, you may submit a data deletion request by emailing us at support@collectedAF.com.

Upon receiving a deletion request, we will delete or anonymize your personal data within 30 days, including your profile information, location history, and any other personally identifiable information. Some data may be retained in anonymized form for analytics purposes or as required by law. We will confirm the completion of your deletion request via email.`
    },
    {
      number: 6,
      icon: FaShieldAlt,
      title: 'Child Safety and Protection (CSAE)',
      content: `At collectedAF, we are committed to ensuring the safety and well-being of children and preventing any misuse of our platform. We strictly prohibit users from creating, uploading, or distributing content that facilitates the exploitation or abuse of children. This includes all child sexual abuse materials (CSAM). Any violation of this policy will result in immediate account termination and may lead to removal from Google Play.

We prohibit the use of our app to endanger children. This includes but is not limited to promoting predatory behavior towards children, such as:
• Inappropriate interaction targeted at a child (for example, groping or caressing)
• Child grooming (for example, befriending a child online to facilitate, either online or offline, sexual contact and/or exchanging sexual imagery with that child)
• Sexualization of a minor (for example, imagery that depicts, encourages or promotes the sexual abuse of children or the portrayal of children in a manner that could result in the sexual exploitation of children)
• Sextortion (for example, threatening or blackmailing a child by using real or alleged access to a child's intimate images)
• Trafficking of a child (for example, advertising or solicitation of a child for commercial sexual exploitation)

We actively monitor and enforce policies to detect and prevent such behavior through moderation tools, reporting mechanisms, and collaboration with law enforcement and organizations specializing in child protection. We will take appropriate action, which may include reporting to the National Center for Missing & Exploited Children (NCMEC), if we become aware of content with child sexual abuse materials.

As a social app, collectedAF complies with Google Play's Child Safety Standards policy. We certify that our app:
• Has Published Standards: We explicitly prohibit Child Sexual Abuse and Exploitation (CSAE) in our publicly accessible terms of service, community guidelines, and this privacy policy
• Provides In-App Reporting: We provide a mechanism within our app for users to submit feedback, concerns, or reports regarding inappropriate content or behavior
• Addresses CSAM: We take appropriate action, including removing CSAM and reporting to authorities, after obtaining actual knowledge of it, in accordance with our published standards and relevant laws
• Complies with Child Safety Laws: We comply with applicable child safety laws and regulations, including having a process in place to report confirmed CSAM to the National Center for Missing and Exploited Children or relevant regional authorities
• Provides a Child Safety Point of Contact: We maintain a designated point of contact at support@collectedAF.com to receive notifications from Google Play about CSAE content and to coordinate enforcement and review procedures

If you encounter any content or activity that appears to involve child endangerment, abuse, exploitation, or CSAE, please report it immediately by contacting us at support@collectedAF.com. Your report will be treated with the utmost urgency and confidentiality. If you believe a child is in danger of or has been subject to abuse, exploitation, or trafficking, please contact your local law enforcement immediately.

We reserve the right to take immediate action against accounts or individuals found to be engaging in such activities, including but not limited to reporting to appropriate authorities, terminating access to our Service, and cooperating fully with law enforcement investigations.`
    },
    {
      number: 7,
      icon: FaShieldAlt,
      title: 'Log Data & Cookies',
      content: `We want to inform you that whenever you use our Service, in a case of an error in the app we collect data and information (through third-party products) on your phone called Log Data. This Log Data may include information such as your device Internet Protocol ("IP") address, device name, operating system version, the configuration of the app when utilizing our Service, the time and date of your use of the Service, and other statistics.

Cookies are files with a small amount of data that are commonly used as anonymous unique identifiers. These are sent to your browser from the websites that you visit and are stored on your device's internal memory. This Service does not use these "cookies" explicitly. However, the app may use third-party code and libraries that use "cookies" to collect information and improve their services.`
    },
    {
      number: 8,
      icon: FaShieldAlt,
      title: 'Service Providers & Security',
      content: `We may employ third-party companies and individuals to facilitate our Service, provide the Service on our behalf, perform Service-related services, or assist us in analyzing how our Service is used. These third parties have access to their Personal Information to perform tasks assigned to them on our behalf. However, they are obligated not to disclose or use the information for any other purpose.

We value your trust in providing us your Personal Information, thus we are striving to use commercially acceptable means of protecting it. But remember that no method of transmission over the internet, or method of electronic storage is 100% secure and reliable, and we cannot guarantee its absolute security.`
    },
    {
      number: 9,
      icon: FaShieldAlt,
      title: 'SMS Messaging & 2FA Verification',
      content: `collectedAF uses SMS-based messaging for account security and identity verification purposes only. This includes two-factor authentication (2FA) to confirm user logins and safeguard user accounts.

By signing up for our Service and providing your phone number, you consent to receiving one-time passcodes via SMS for secure login. These messages are automated, contain no promotional content, and are essential to the function of the app.

Users can manage their phone number and opt-in status through account settings. For more information, contact us at support@collectedAF.com.`
    },
    {
      number: 10,
      icon: FaShieldAlt,
      title: 'Children\'s Privacy & Policy Changes',
      content: `These Services do not address anyone under the age of 13. We do not knowingly collect personally identifiable information from children under 13 years of age. In the case we discover that a child under 13 has provided us with personal information, we immediately delete this from our servers.

collectedAF enforces strict age-based separation within the app. During registration, every user's age is verified and used to determine their experience tier. Minors (under 18) are placed in a controlled environment where they can only discover, interact with, and communicate with other verified minors. Adult users are completely invisible to minors and vice versa. This architectural separation is enforced at the platform level and cannot be bypassed by individual users.

Key safeguards include:
• Proximity discovery, collections, and all social features are filtered by age group
• Messaging and communication channels are restricted to same-age-group peers only
• Content visibility, profiles, and user cards are segmented so minors and adults never appear in each other's feeds
• Age misrepresentation is a terminable offense and is actively monitored

We may update our Privacy Policy from time to time. Thus, you are advised to review this page periodically for any changes. We will notify you of any changes by posting the new Privacy Policy on this page. This policy is effective as of 2026-03-11.

If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at support@collectedAF.com.`
    }
  ];

  return (
    <div className="z-20 flex-shrink-0 py-8">
      <div className="relative min-h-[350px] lg:h-[calc(100vh-8rem)]">
        {/* Background Giant C */}
        <div 
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[30rem] opacity-25 pointer-events-none"
          style={{ 
            fontFamily: '"Anton", sans-serif',
            background: 'linear-gradient(135deg, #3b82f6 0%, #14b8a6 45%, #fbbf24 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            filter: 'drop-shadow(0 20px 60px rgba(0,0,0,0.35))'
          }}
        >
          C
        </div>
        
        {/* Content */}
        <div className="relative h-full flex items-center justify-center">
          <div className="colorful-ring bg-BLACK/92 backdrop-blur-xl rounded-3xl p-6 lg:p-12 w-full h-auto lg:h-full transition-colors shadow-2xl flex flex-col">
            <h2 className="text-4xl lg:text-5xl font-bold text-WHITE mb-3 text-center">Privacy Policy</h2>
            <p
              className="text-lg font-bold mb-6 text-center"
              style={{
                background: 'linear-gradient(90deg, #ffffff 0%, #0066cc 50%, #ffffff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Your privacy and data security matter to us
            </p>
            
            {/* Scroll Indicator */}
            <div className={`absolute top-32 left-0 right-0 h-12 bg-gradient-to-b from-ORANGE/30 to-transparent pointer-events-none transition-opacity duration-300 z-10 ${showScrollIndicator ? 'opacity-100' : 'opacity-0'}`}>
              <div className="flex justify-center pt-2">
                <FaChevronUp className="text-ORANGE animate-bounce" size={24} />
              </div>
            </div>
            
            {/* Scrollable Accordions */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2" onScroll={handleScroll}>
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={index} className="bg-WHITE/5 rounded-xl border border-WHITE/10 hover:border-ORANGE/50 transition-colors">
                    {/* Accordion Header */}
                    <button
                      onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                      className="w-full flex items-center gap-3 p-4 text-left"
                    >
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-WHITE/10 flex items-center justify-center border-2 border-WHITE/25">
                        <Icon className="text-ORANGE" size={20} />
                      </div>
                      <div className="flex-1 flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-ORANGE flex items-center justify-center flex-shrink-0">
                          <span className="text-WHITE text-lg font-bold">{section.number}</span>
                        </div>
                        <h4 className="text-WHITE text-lg font-bold">{section.title}</h4>
                      </div>
                      {openAccordion === index ? (
                        <FaChevronUp className="text-ORANGE" size={20} />
                      ) : (
                        <FaChevronDown className="text-WHITE/50" size={20} />
                      )}
                    </button>
                    
                    {/* Accordion Content */}
                    {openAccordion === index && (
                      <div className="px-4 pb-4 pt-0">
                        <p className="text-WHITE/70 text-sm leading-relaxed whitespace-pre-line">
                          {section.content}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicySection;
